import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  loadResumeSale,
  ResumeSaleError,
  type ResumeSaleData,
  type ResumeSaleFailure,
  type ResumeSaleState,
} from "@/lib/sales/resumeSale";

export interface UseResumeSaleOptions {
  onLoaded: (data: ResumeSaleData) => void;
  onError: (reason: ResumeSaleFailure) => void;
}

export interface UseResumeSaleResult {
  /** Enquanto true, o PDV mostra esqueleto: carrinho vazio piscando confunde. */
  loading: boolean;
}

/**
 * Lê `location.state.resumeSale`, carrega a venda e devolve os dados uma vez só.
 *
 * O state da rota é limpo depois de consumido para que um F5 ou um "voltar" não
 * recarreguem a mesma venda por cima do que o operador já estiver fazendo.
 */
export function useResumeSaleFromLocation({
  onLoaded,
  onError,
}: UseResumeSaleOptions): UseResumeSaleResult {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as { resumeSale?: ResumeSaleState } | null)?.resumeSale ?? null;
  const [loading, setLoading] = useState(false);

  // Os callbacks mudam a cada render da página; guardá-los em ref evita
  // recarregar a venda a cada re-render.
  const handlers = useRef({ onLoaded, onError });
  handlers.current = { onLoaded, onError };

  useEffect(() => {
    if (!state) return;

    let cancelled = false;
    setLoading(true);

    void loadResumeSale(state)
      .then((data) => {
        if (cancelled) return;
        handlers.current.onLoaded(data);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        handlers.current.onError(error instanceof ResumeSaleError ? error.reason : "load_failed");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        navigate("/sales/new", { replace: true, state: null });
      });

    return () => {
      cancelled = true;
    };
    // `state` vem do location; só muda quando outra tela navega para cá.
  }, [navigate, state]);

  return { loading };
}
